const BASE_URL = '/api/csearch/'

export const useCustomSearch = () => {
  const { fetchGet, fetchPost } = useCustomFetch()
  const { getDatabase } = useDatabase()
  const { getAsync } = useDataStore()
  const { $getContentsCodeLang } = useNuxtApp()

  function getCustomSearch(id: string): Promise<CustomSearchDefinition> {
    if (id === systemCrossSearchCs().id)
      return new Promise<CustomSearchDefinition>((resolve, reject) => {
        resolve(systemCrossSearchCs())
      })
    if (id === systemGallerySearchCs().id)
      return new Promise<CustomSearchDefinition>((resolve, reject) => {
        resolve(systemGallerySearchCs())
      })
    return fetchGet<CustomSearchDefinition>(BASE_URL + id)
  }

  function deleteCustomSearch(c: CustomSearchDefinition): Promise<JpsResponse> {
    return fetchPost(BASE_URL + c.id + '/delete')
  }

  function getCustomSearchForDb(dbId: string): Promise<CustomSearchDefinition> {
    return getCustomSearch(dbId + '-default')
  }

  function searchCustomSearch(
    query: SearchQuery,
  ): Promise<SearchResult<CustomSearchDefinition>> {
    return fetchGet(BASE_URL + 'search' + toSearchQueryString(query))
  }

  function createCustomSearch(
    def: CustomSearchDefinition,
  ): Promise<CustomSearchDefinition> {
    return fetchPost(BASE_URL + 'create', def)
  }

  function updateCustomSearch(
    def: CustomSearchDefinition,
  ): Promise<CustomSearchDefinition> {
    return fetchPost(BASE_URL + 'update', def)
  }

  class QueryExpression {
    cname: LangString
    q: { label: LangString; value: LangString[] }[] = []

    toString(ls: (ls: LangString) => string) {
      let str = ''
      this.q.forEach((q, i) => {
        if (i !== 0) str += '/'
        str += ls(q.label) + ':' + q.value.map(v => ls(v)).join(',')
      })
      return ls(this.cname) + (str ? '(' + str + ')' : '')
    }
  }

  async function getQueryExpression(q: SearchQuery): Promise<QueryExpression> {
    const cs = await getCustomSearch(q.csid)
    if (cs) {
      const ret: QueryExpression = new QueryExpression()
      ret.cname = cs.name

      //キーワード
      if (!isEmpty(q.keyword)) {
        ret.q.push({
          label: { ja: 'キーワード', en: 'Keyword' },
          value: [
            {
              ja: q.keyword.join(' '),
            },
          ],
        })
      }

      //画像
      if (!isEmpty(q.image)) {
        ret.q.push({
          label: { ja: '画像', en: 'Image' },
          value: [
            {
              ja: isExternalFeature(q.image[0])
                ? 'ファイル'
                : 'ID:' + q.image[0],
              en: isExternalFeature(q.image[0]) ? 'File' : 'ID:' + q.image[0],
            },
          ],
        })
      }
      //詳細項目
      await processQuery(ret.q, cs, q.query)
      await processQuery(ret.q, cs, q.filter)
      await processQuery(ret.q, cs, q.range)

      return ret
    }
    return null
  }

  async function processQuery(
    v: any[],
    cs: CustomSearchDefinition,
    q: { [key: string]: any },
  ) {
    if (!isEmpty(q)) {
      const keys = Object.keys(q)
      for (let i = 0; i < keys.length; i++) {
        if (!isEmpty(q[keys[i]])) {
          const value = await processField(cs, keys[i], q[keys[i]])
          if (value) v.push(value)
        }
      }
    }
  }

  async function processField(
    cs: CustomSearchDefinition,
    key: string,
    values: any,
  ) {
    const field = cs.searchFields.find(f => f.key === key)
    if (field) {
      const valueLs: LangString[] = []
      switch (field.type) {
        case 'code':
          const code: Code = await getAsync('code', field.codeKey)
          for (let i = 0; i < values.length; i++) {
            const key = values[i].replace(/^[+-]/, '')
            const cv = code.values.find(v => v.code === key)
            if (cv) valueLs.push(cv.value)
          }
          break
        case 'contents':
          const contentsCodeLang = $getContentsCodeLang()
          for (let i = 0; i < values.length; i++) {
            const cl = contentsCodeLang[values[i].replace(/^[+-]/, '')]
            if (cl) valueLs.push(cl)
          }
          break
        case 'database':
          for (let i = 0; i < values.length; i++) {
            const db = await getDatabase(values[i])
            valueLs.push(db.name)
          }
          break
        case 'keyword':
          values.forEach(v => valueLs.push({ ja: v }))
          break
        case 'last_update':
          // valueLs.push(updateSearchFieldValues["l-" + values[0].from]);
          break
      }

      return {
        label: field.name,
        value: valueLs,
      }
    } else {
      return {
        label: { ja: 'その他の項目', en: 'Other Field' },
        value: values.map(v => {
          v
        }),
      }
    }
    return null
  }

  function getTargetDbIds(cs: CustomSearchDefinition) {
    if (cs.databaseIds != null && cs.databaseIds.length > 0)
      return cs.databaseIds.sort()
    return uniqueArray(
      [
        ...cs.keywordFields,
        ...flatArray(
          cs.searchFields ? cs.searchFields.map(sf => sf.fields) : [],
        ),
      ]
        .filter(
          f =>
            f != null &&
            f !== '' &&
            f !== 'keywords' &&
            !/^common\.|^sindex./.test(f) &&
            /-/.test(f),
        )
        .map(f => f.split(/-/)[0]),
    ).sort()
  }

  return {
    getCustomSearch,
    deleteCustomSearch,
    getCustomSearchForDb,
    searchCustomSearch,
    createCustomSearch,
    updateCustomSearch,
    getQueryExpression,
    getTargetDbIds,
  }
}
